<template>
  <div class="holiday-members-setup">
    <div class="text-grey mb-15">Choose how you want to assign members into holidays</div>
    <div class="mb-15">
      <ValidationProvider
        v-slot="{ errors }"
        mode="eager"
        rules="required"
        name="members_added_via"
        :model-value="holiday.members_added_via"
      >
        <div :class="{ 'has-error': errors[0] }">
          <div class="d-flex">
            <label class="control-label required"> Add members through </label>
            <i
              v-tooltip="{ placement: 'top' }"
              title="Assign members in the most convenient way to set up holidays"
              class="hi hi-info-filled hi-12 hi-tooltip text-grey ml-5"
            />
          </div>
          <AppSelect
            :value="holiday.members_added_via"
            :options="addMembersBy"
            :select2-options="membersAddedBySelect2Options"
            data-testid="holiday-members-select"
            @input="handleMembersAddedByField($event, holiday.members_added_via)"
          />
          <span class="help-block">{{ errors[0] }}</span>
        </div>
      </ValidationProvider>
    </div>

    <div v-if="memberListSelected">
      <div class="mb-15">
        <AppMembersSelector
          ref="members-user-selector"
          :organization-id="organizationId"
          :resource-id="holidayId"
          :resource="holidayResource"
          multiple
          show-select-all-trigger
          @submit-selections="updateMemberSelections"
        >
          <template #title>
            <label class="control-label">Members</label>
          </template>
        </AppMembersSelector>
      </div>
    </div>

    <div v-if="csvTypeSelected">
      <div class="mb-15">
        <label class="control-label required">File</label>
        <div class="text-grey mb-15">Only include members' emails on CSV upload</div>
        <AppFileImport
          :import-file="false"
          :file-params="fileParams"
          :show-label="false"
          file-types=" .CSV"
          mime-types=".csv"
          :loading="csvLoading"
          :template-url="templateUrl"
          @upload-file="updateCsvFile($event)"
          @remove-file="removeCsvFile"
        />
        <div
          v-if="importCsvError && !invalidEmailsUrl"
          class="dialog-warning mt-15"
        >
          <i class="d-flex align-items-center hi hi-warning hi-12 text-danger mr-10"></i>
          <div>{{ importCsvError }}</div>
        </div>

        <div
          v-if="invalidEmailsUrl"
          class="dialog-warning orange mt-15"
        >
          <i class="d-flex align-items-center hi hi-warning hi-12 text-orange mr-10"></i>
          <div>
            Unable to import {{ invalidEmailsSize }} members. <a :href="invalidEmailsUrl">Download the CSV</a> with
            members' emails for review and re-upload the original file.
          </div>
        </div>
      </div>
    </div>

    <div v-if="countryAndEmploymentTypeSelected">
      <div class="d-flex align-items-center dialog-warning blue mb-15 rounded p-10">
        <i class="hi hi-info-filled hi-16"></i>
        <div>
          This policy is based on <b>Home address - country</b> and <b>Employment type</b> fields.
          <a
            :href="membersUrl"
            target="_blank"
          >
            Member profiles
          </a>
          must be up-to-date to ensure proper policy assignment.
        </div>
      </div>

      <div class="row">
        <div class="col-12 col-md-6 country">
          <ValidationProvider
            v-slot="{ errors }"
            mode="eager"
            name="countries"
            :model-value="holiday.membership_rules.countries"
          >
            <div :class="{ 'has-error': errors[0] }">
              <label class="control-label required"> Home address - Country </label>
              <a
                class="pull-right select-all"
                @click="
                  () => {
                    $refs.countriesDropdown.selectAll();
                  }
                "
              >
                Select all
              </a>
              <div class="country-select">
                <AppSelect
                  ref="countriesDropdown"
                  :value="holiday.membership_rules.countries"
                  :options="membershipRuleOptions.countries"
                  :select2-options="countrySelect2Options"
                  compact
                  label="countries"
                  @input="updateMembers('membership_rules_countries', $event)"
                />
                <span class="help-block">{{ errors[0] }}</span>
              </div>
            </div>
          </ValidationProvider>
        </div>

        <div class="col-12 col-md-6 employment">
          <ValidationProvider
            v-slot="{ errors }"
            mode="eager"
            name="employment_types"
            :model-value="holiday.membership_rules.employment_types"
          >
            <div :class="{ 'has-error': errors[0] }">
              <label class="control-label required"> Employment type </label>
              <a
                class="pull-right select-all"
                @click="
                  () => {
                    $refs.employmentDropdown.selectAll();
                  }
                "
              >
                Select all
              </a>
              <div class="employment-type-select">
                <AppSelect
                  ref="employmentDropdown"
                  :value="holiday.membership_rules.employment_types"
                  :options="membershipRuleOptions.employment_types"
                  :select2-options="employmentTypeSelect2Options"
                  compact
                  label="employment types"
                  @input="updateMembers('membership_rules_employment_types', $event)"
                />
                <span class="help-block">{{ errors[0] }}</span>
              </div>
            </div>
          </ValidationProvider>
        </div>
      </div>

      <ValidationProvider
        v-slot="{ errors }"
        mode="eager"
        name="membership_rules"
      >
        <div
          v-if="showMembershipRuleError(errors)"
          class="d-flex align-items-center dialog-warning red mt-15"
        >
          <i class="hi hi-warning hi-14 text-danger mr-10"></i>
          <div>At least one member field must be selected to continue</div>
        </div>
      </ValidationProvider>
    </div>

    <div
      v-if="showOverlapShiftWarning"
      class="dialog-warning orange mb-15"
    >
      <i class="hi hi-info-filled hi-12"></i>
      <div>
        {{ overlapShiftText }}
      </div>
    </div>

    <div>
      <div
        v-if="!countryAndEmploymentTypeSelected"
        class="checkbox"
      >
        <AppCheckbox
          :value="holiday.auto_add_new_users"
          :disabled="disableAutoAddOption"
          @input="updateHoliday({ field: 'auto_add_new_users', value: $event })"
        >
          <div
            v-tooltip="{ placement: 'top' }"
            class="message"
            :data-original-title="autoAddTooltipText"
          >
            Automatically add all new members to this holiday
          </div>
        </AppCheckbox>
      </div>

      <div
        v-if="showNextOccurrenceWarning"
        class="d-flex align-items-center dialog-warning orange mt-15"
      >
        <i class="hi hi-warning hi-14 text-orange mr-10"></i>
        <div>Since this holiday already occurred this year, new members will be added to next year's holiday.</div>
      </div>
    </div>
  </div>
</template>

<script>
import { MEMBERS_EMAIL_FILE_IMPORT_TEMPLATE_URL, EMPLOYMENT_TYPE_OPTIONS } from '~/app_constants';
import { HolidaysResource } from '~/resources';

export default {
  name: 'HolidayMembersSetupForm',
  props: {
    currentMembers: {
      type: Array,
      default: () => [],
    },
    holiday: {
      type: Object,
      required: true,
    },
    overlapShiftCount: {
      type: Number,
      default: 0,
    },
    organizationId: {
      type: Number,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    membersReload: {
      type: Boolean,
      default: false,
    },
    organizationTz: {
      type: String,
      default: 'UTC',
    },
    editing: {
      type: Boolean,
      default: false,
    },
    countryList: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    fileParams: {},
    invalidEmailsUrl: null,
    invalidEmailsSize: null,
    importCsvError: null,
    csvLoading: false,
    tooltipText: 'Assign members in the most convenient way to set up holidays',
    membershipRulesErrors: null,
  }),
  computed: {
    addMembersBy() {
      const disabledHint = "Can't change the way in which members were assigned since all data will be lost.";
      const tooltipOptions = { placement: 'right' };

      return [
        {
          id: 'list',
          text: 'List of members',
          description: 'List of all members belonging to the organization',
          disabled: this.editing && this.holiday.has_membership_rules,
          disabledHint,
          tooltipOptions,
        },
        {
          id: 'country_and_employment',
          text: 'Home country and Employment type',
          description: 'Group by location and/or employment type',
          disabled: this.editing && !this.holiday.has_membership_rules,
          disabledHint,
          tooltipOptions,
        },
        {
          id: 'csv',
          text: 'Import CSV',
          description: 'Upload CSV file',
          disabled: this.editing && this.holiday.has_membership_rules,
          disabledHint,
          tooltipOptions,
        },
      ];
    },
    membershipRuleOptions() {
      return { countries: this.countryList, employment_types: EMPLOYMENT_TYPE_OPTIONS };
    },
    disableAutoAddOption() {
      // We should disable the auto-add checkbox when editing a past holiday
      if (this.editing && this.pastDateSelected) {
        return true;
      }

      // When creating a holiday, we should only disbale the auto-add checkbox if
      // the holiday is non-recurring and in a past date
      return this.pastDateSelected && !this.holiday.recurring;
    },
    showNextOccurrenceWarning() {
      // Only show the warning when creating a recurring holiday in the past with the auto-add checkbox set to true
      return !this.editing && this.holiday.auto_add_new_users && this.holiday.recurring && this.pastDateSelected;
    },
    pastDateSelected() {
      return moment.tz(this.holiday.date, this.organizationTz).isBefore(moment().tz(this.organizationTz), 'day');
    },
    holidayResource() {
      return HolidaysResource.members;
    },
    holidayId() {
      return this.holiday.id ? Number(this.holiday.id) : null;
    },
    shiftText() {
      return this.overlapShiftCount > 1 ? 'shifts' : 'shift';
    },
    overlapShiftText() {
      return `This holiday overlaps with ${this.overlapShiftCount} ${this.shiftText}`;
    },
    showOverlapShiftWarning() {
      if (this.holiday.members_added_via === 'csv') {
        return this.overlapShiftCount > 0 && this.fileParams.id && !this.csvLoading;
      }

      return this.overlapShiftCount > 0 && !this.loading;
    },
    autoAddTooltipText() {
      if (!this.disableAutoAddOption) {
        return '';
      }

      return this.editing && this.pastDateSelected
        ? "Can't change this setting on past holidays"
        : "Can't automatically add new members because the holiday has already occurred and it doesn't repeat annually";
    },
    memberListSelected() {
      return this.holiday.members_added_via === 'list' && !this.loading;
    },
    csvTypeSelected() {
      return this.holiday.members_added_via === 'csv';
    },
    countryAndEmploymentTypeSelected() {
      return this.holiday.members_added_via === 'country_and_employment';
    },
    templateUrl() {
      return MEMBERS_EMAIL_FILE_IMPORT_TEMPLATE_URL;
    },
    membersUrl() {
      return `/organizations/${this.organizationId}/members`;
    },
    membersSelect2Options() {
      return this.generateSelect2Options('Select member(s)', false);
    },
    employmentTypeSelect2Options() {
      return this.generateSelect2Options('Select employment type', true);
    },
    countrySelect2Options() {
      return this.generateSelect2Options('Select home country', true);
    },
    membersAddedBySelect2Options() {
      return { placeholder: 'Select how to add members', width: '100%' };
    },
  },
  watch: {
    membersReload(value) {
      if (value) {
        this.resetMemberOptions();
      }
    },
  },
  methods: {
    generateSelect2Options(placeholder, allowClear) {
      return { multiple: true, placeholder, allowClear, width: '100%' };
    },
    updateHoliday({ field, value }) {
      this.$emit('holiday-updated', field, value);
    },
    updateMembers(field, value) {
      this.$emit('members-updated', field, value);
    },
    handleMembersAddedByField(value) {
      this.resetMemberOptions();
      this.updateHoliday({ field: 'members_added_via', value });

      // holidays that have membership rules will always have auto add enabled
      if (this.holiday.members_added_via === 'country_and_employment') {
        this.updateHoliday({ field: 'auto_add_new_users', value: true });
      }

      if (this.holiday.members_added_via === 'csv') {
        this.$emit('disable-save-button', true);
      } else {
        this.$emit('disable-save-button', false);
      }
    },
    async updateCsvFile(file) {
      try {
        this.csvLoading = true;
        this.fileParams = { id: file.name, metadata: { filename: file.name } };

        const params = { organizationId: this.organizationId };
        const formData = new FormData();

        if (this.holiday.id) {
          formData.append('id', this.holiday.id);
        }

        formData.append('csv_file', file);

        const response = await HolidaysResource.checkCsvFile(
          params,
          {},
          { abortReload: true, data: formData, processData: false, contentType: false },
        );

        if (response.errors) {
          this.importCsvError = response.errors.join(', ');

          return;
        }

        if (response.user_ids) {
          this.updateMembers('holiday_user_ids', response.user_ids);

          if (response.invalid_emails && response.invalid_emails.length > 0) {
            const csv = response.invalid_emails
              .map((item) => [item])
              .map((fields) => fields.join(','))
              .join('\n');
            const data = 'data:text/csv;charset=utf-8,' + csv;

            this.invalidEmailsUrl = encodeURI(data);
            this.invalidEmailsSize = response.invalid_emails.length;
          }

          if (response.user_ids.length > 0) {
            this.$emit('disable-save-button', false);
          } else {
            this.importCsvError = 'No members will be added as they already belong to this holiday';
          }
        }
      } catch (error) {
        if (![401, 403].includes(error.status)) {
          this.importCsvError = 'There was an error processing your import. Please check the file and try again.';
        }
      } finally {
        this.csvLoading = false;
      }
    },
    removeCsvFile() {
      this.resetMemberOptions();
      this.$emit('disable-save-button', true);
    },
    resetCsv() {
      this.invalidEmailsUrl = null;
      this.invalidEmailsSize = null;
      this.importCsvError = null;
      this.fileParams = {};
      this.csvLoading = false;
    },
    resetHolidayMembers() {
      this.updateMembers('holiday_user_ids', this.currentMembers);
      this.updateHoliday({ field: 'auto_add_new_users', value: false });
    },
    resetMemberOptions() {
      this.resetCsv();
      this.resetHolidayMembers();
      if (this.holiday.membership_rules) {
        this.resetMembershipRules();
      }
    },
    resetMembershipRules() {
      this.updateMembers('membership_rules_countries', []);
      this.updateMembers('membership_rules_employment_types', []);
    },
    async updateMemberSelections({ members, options = { select_all: false, clear_all: false } }) {
      this.updateMembers('members-list-data', { members, options });
    },
    showMembershipRuleError(errors) {
      return errors.includes('at least one rule must be selected');
    },
  },
};
</script>
